import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Moment } from 'moment';
import { makeStyles } from '@mui/styles';
import { TextField, TextFieldProps } from '@mui/material';
import { DatePicker as MUIDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { COLORS } from 'theme/MainTheme';
import { StateSetter } from 'types/utils/React';

export enum DateTimePickerType {
  Date,
  Time,
}

type GetDateTimeBasePropsReturn = {
  cancelText: ReactNode;
  toolbarTitle: ReactNode;
};

export const getDateTimeBaseProps = (
  type: DateTimePickerType,
): GetDateTimeBasePropsReturn => {
  const baseProps = {
    cancelText: <FormattedMessage id="button.cancel" />,
  };

  if (type === DateTimePickerType.Time) {
    return {
      ...baseProps,
      toolbarTitle: <FormattedMessage id="select.hour" />,
    };
  } else {
    return {
      ...baseProps,
      toolbarTitle: <FormattedMessage id="select.date" />,
    };
  }
};


const useStyles = makeStyles(() => ({
  datePicker: {
    '& .Mui-selected': {
      backgroundColor: COLORS.BLUE,

      '&:focus': {
        backgroundColor: COLORS.BLUE,
      },

      '&:hover': {
        backgroundColor: COLORS.BLUE,
      },
    },
  },
  textField: {
    color: COLORS.BLUE,
    width: '100%',

    '& .MuiOutlinedInput-root, &.MuiOutlinedInput-root': {
      '& input': {
        color: COLORS.BLUE,
      },
      '& fieldset': {
        border: `1px solid ${COLORS.BLUE}`,
      },
      '&:hover fieldset': {
        border: `1px solid ${COLORS.BLUE}`,
      },
      '&.Mui-focused fieldset': {
        border: `1px solid ${COLORS.BLUE}`,
      },
      '& .MuiSvgIcon-root': {
        color: COLORS.BLUE,
      },
    },
  },
}), { name: 'DatePicker' });

export type DatePickerProps = {
  className?: string;
  date: Moment | null;
  renderInput?: (props: TextFieldProps) => React.ReactElement;
  setDate: StateSetter<Moment | null>;
};

const DatePicker = ({
  className,
  date,
  renderInput,
  setDate,
}: DatePickerProps): JSX.Element => {
  const classes = useStyles();
  const actualRenderInput =
    renderInput ||
    ((params) => (
      <TextField className={classes.textField} size="small" {...params} />
    ));

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <MUIDatePicker
        {...getDateTimeBaseProps(DateTimePickerType.Date)}
        className={className}
        PaperProps={{ className: classes.datePicker }}
        disablePast
        openTo="month"
        views={['year', 'month', 'day']}
        value={date}
        onChange={setDate}
        renderInput={actualRenderInput}
      />
    </LocalizationProvider>
  );
};

export default DatePicker;
